import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { Subscription } from 'rxjs';
import { Company } from '../../company/company.model';
import { TenantScope } from '../../company/tenant-access.model';
import { TenantScopeService } from '../../company/tenant-scope.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { DashboardCompanyScopeSelectorComponent } from '../../shared/page-layout/dashboard-company-scope-selector.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditDashboardSummary } from '../audit.models';
import { AuditService } from '../audit.service';
import { auditNavItems, formatAuditDate } from '../audit-ui';

interface AuditFoundationItem {
  title: string;
  subtitle: string;
  status: string;
  icon: string;
  route: string;
  caption: string;
  queryParams?: Record<string, string>;
}

@Component({
  selector: 'app-audit-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    ProgressBarModule,
    TagModule,
    DashboardCompanyScopeSelectorComponent,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  template: `
    <app-page-shell>
      <app-page-hero eyebrow="Governance" title="LogiLink Audit" subtitle="Tenant audit foundation for cases, evidence, findings, corrective actions, and reports.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="item.route !== '/audit'" severity="secondary"></a>
          }
        </div>
        <div actions>
          <button pButton type="button" icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="load()"></button>
        </div>
      </app-page-hero>

      @if (showGroupCompanySelector) {
        <app-dashboard-company-scope-selector
          [companyGroupName]="activeGroupName"
          [memberCompanies]="groupCompanies"
          [selectedCompanyId]="selectedGroupCompanyId"
          (selectedCompanyIdChange)="onDashboardCompanyScopeChange($event)">
        </app-dashboard-company-scope-selector>
      }

      @if (loading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
      }

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <section class="audit-grid" aria-label="Audit foundation modules">
        @for (item of foundationItems; track item.title) {
          <app-page-data-panel [title]="item.title" [subtitle]="item.subtitle" [resultText]="item.status">
            <div class="foundation-row">
              <i [class]="item.icon"></i>
              <span>{{ item.caption }}</span>
            </div>
            <div class="foundation-actions">
              <a pButton [routerLink]="item.route" [queryParams]="item.queryParams || null" label="Open" icon="pi pi-arrow-right" iconPos="right" [outlined]="true" severity="secondary"></a>
            </div>
          </app-page-data-panel>
        }
      </section>
    </app-page-shell>
  `,
  styles: [`
    .audit-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 1rem;
    }

    .foundation-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      min-height: 3rem;
      color: #334155;
      font-weight: 600;
    }

    .foundation-row i {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      border-radius: 8px;
      color: #0f766e;
      background: #ecfdf5;
      font-size: 1rem;
      flex: 0 0 auto;
    }

    .foundation-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 1rem;
    }
  `]
})
export class AuditDashboardComponent implements OnInit, OnDestroy {
  private readonly auditService = inject(AuditService);
  private readonly permissionService = inject(PermissionService);
  private readonly tenantScopeService = inject(TenantScopeService);

  loading = false;
  summary: AuditDashboardSummary = {};
  private readonly subscriptions = new Subscription();
  private companyId: number | null = null;
  private companyGroupId: number | null = null;
  activeGroupName = '';
  groupCompanies: Company[] = [];
  selectedGroupCompanyId: number | null = null;
  readonly visibleNavItems = auditNavItems.filter(item =>
    this.permissionService.hasPermission(item.permission, item.route === '/audit' ? 'ANY' : undefined)
  );

  readonly foundationItems: AuditFoundationItem[] = [
    {
      title: 'Audit Cases',
      subtitle: 'Case records and lifecycle counters.',
      status: 'Phase 2',
      icon: 'pi pi-briefcase',
      route: '/audit/cases',
      caption: 'Create manual cases, start execution, save checklist responses, and review outcomes.'
    },
    {
      title: 'Templates',
      subtitle: 'Reusable audit checklist versions.',
      status: 'Phase 2',
      icon: 'pi pi-list-check',
      route: '/audit/templates',
      caption: 'Build draft sections, questions, scoring weights, mandatory controls, and evidence rules.'
    },
    {
      title: 'Findings',
      subtitle: 'Nonconformities and observations.',
      status: 'Phase 2',
      icon: 'pi pi-exclamation-triangle',
      route: '/audit/findings',
      caption: 'Track manually raised findings and findings generated from failed checklist controls.'
    },
    {
      title: 'Corrective Actions',
      subtitle: 'CAPA ownership and verification.',
      status: 'Phase 3',
      icon: 'pi pi-check-square',
      route: '/audit/actions',
      caption: 'Assign owners, collect evidence metadata, submit actions, and verify or reject completion.'
    },
    {
      title: 'Action Report',
      subtitle: 'Action aging and verification data.',
      status: 'Phase 3',
      icon: 'pi pi-table',
      route: '/audit/reports/corrective-actions',
      caption: 'Review export-ready corrective action status, priority, owners, due dates, and source links.'
    },
    {
      title: 'Overdue Actions',
      subtitle: 'Owner work past due date.',
      status: 'Phase 3',
      icon: 'pi pi-calendar-times',
      route: '/audit/actions',
      queryParams: { aging: 'OVERDUE' },
      caption: 'Open the corrective action board filtered to overdue, non-terminal owner work.'
    },
    {
      title: 'Rejected Actions',
      subtitle: 'Reviewer rejected CAPA work.',
      status: 'Phase 3',
      icon: 'pi pi-times-circle',
      route: '/audit/actions',
      queryParams: { status: 'REJECTED' },
      caption: 'Open the corrective action board filtered to rejected corrective actions.'
    },
    {
      title: 'Overdue Findings',
      subtitle: 'Finding aging report.',
      status: 'Phase 3',
      icon: 'pi pi-clock',
      route: '/audit/reports/overdue-findings',
      caption: 'Find overdue findings and the open or overdue corrective actions attached to them.'
    },
    {
      title: 'Evidence Locker',
      subtitle: 'Source snapshots and metadata.',
      status: 'Phase 4',
      icon: 'pi pi-database',
      route: '/audit/evidence',
      caption: 'Review evidence metadata harvested from source records, manual uploads, and domain timelines.'
    },
    {
      title: 'Programs',
      subtitle: 'Reusable audit program setup.',
      status: 'Phase 2',
      icon: 'pi pi-sitemap',
      route: '/audit/programs',
      caption: 'Define tenant audit programs by module, audit type, scope, frequency, and risk.'
    }
  ];

  ngOnInit(): void {
    this.subscriptions.add(this.tenantScopeService.activeScope$.subscribe(scope => this.handleScopeChange(scope)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Cases', value: this.summary.caseCount ?? 0, tone: 'info' },
      { label: 'Findings', value: this.summary.findingCount ?? 0, tone: 'warn' },
      { label: 'Actions', value: this.summary.actionCount ?? 0, tone: 'success' },
      { label: 'Overdue Actions', value: this.summary.overdueActionCount ?? 0, tone: 'danger' },
      { label: 'Rejected Actions', value: this.summary.rejectedActionCount ?? 0, tone: 'warn' },
      { label: 'Critical Findings', value: this.summary.openCriticalFindingCount ?? 0, tone: 'danger' },
      { label: 'Reports', value: this.summary.reportCount ?? 0 },
      { label: 'Last Event', value: formatAuditDate(this.summary.lastAuditEventAt), hint: 'Audit timeline' }
    ];
  }

  get showGroupCompanySelector(): boolean {
    return !!this.companyGroupId && this.groupCompanies.length > 0;
  }

  load(): void {
    if (!this.companyId && !this.companyGroupId) {
      this.summary = {};
      return;
    }
    this.loading = true;
    this.auditService.dashboardSummary(
      this.companyId ?? undefined,
      this.companyGroupId ?? undefined,
      this.companyGroupId ? (this.selectedGroupCompanyId ?? undefined) : undefined
    ).subscribe({
      next: response => {
        this.summary = response.data ?? {};
      },
      error: () => {
        this.summary = {};
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  onDashboardCompanyScopeChange(companyId: number | null): void {
    this.selectedGroupCompanyId = companyId;
    this.load();
  }

  private handleScopeChange(scope: TenantScope | null): void {
    const nextCompanyId = scope?.type === 'COMPANY' ? scope.companyId : null;
    const nextCompanyGroupId = scope?.type === 'GROUP' ? scope.companyGroupId : null;
    this.activeGroupName = scope?.type === 'GROUP' ? scope.companyGroupName : '';
    this.groupCompanies = scope?.type === 'GROUP' ? (scope.memberCompanies ?? []) : [];
    if (!this.groupCompanies.some(company => company.id === this.selectedGroupCompanyId)) {
      this.selectedGroupCompanyId = null;
    }
    if (this.companyId === nextCompanyId && this.companyGroupId === nextCompanyGroupId) {
      return;
    }

    this.companyId = nextCompanyId;
    this.companyGroupId = nextCompanyGroupId;
    this.selectedGroupCompanyId = null;
    this.load();
  }

  protected readonly appPermissions = app_permissions;
}
